import { CSSProperties, ComponentPropsWithoutRef, FC } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import ModeIcon from '@mui/icons-material/Mode';
import { IconButton, Paper, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import IconDisplay from '@/shared/components/utilities/IconDisplay';

type TileProps = {
  title: string;
  icon: string;
  index?: number;
  description?: string;
  isAddTileItem?: boolean;
  columnsLength?: number;
  styles?: CSSProperties;
  mode?: 'idle' | 'edit';
  containerProps: ComponentPropsWithoutRef<typeof Paper>;
  onEditTile?: () => void;
  onRemoveTile?: () => void;
};

const Tile: FC<TileProps> = ({
  title,
  icon,
  description,
  isAddTileItem,
  styles,
  columnsLength = 1,
  mode = 'idle',
  index,
  containerProps,
  onEditTile,
  onRemoveTile,
}) => {
  const ANIMATION_TIME = 1;
  const DELAY_ANIMATION_TIME = ANIMATION_TIME / columnsLength;

  return (
    <Paper
      sx={{
        p: 2,
        position: 'relative',
        cursor: 'pointer',
        height: '100%',
        ...styles,
        ...(mode === 'edit' && index && !isAddTileItem
          ? {
              'animation': `waveReverse ${ANIMATION_TIME}s infinite ease-in-out`,
              'animationDelay': `${index * DELAY_ANIMATION_TIME}s`,

              '@keyframes wave': {
                '0%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(2px)',
                },
                '100%': {
                  transform: 'translateY(0)',
                },
              },
              '@keyframes waveReverse': {
                '0%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(-4px)',
                },
                '100%': {
                  transform: 'translateY(0)',
                },
              },
            }
          : {}),
      }}
      {...containerProps}
    >
      <Typography variant='subtitle1'>{title}</Typography>
      <IconDisplay
        iconName={icon}
        sx={{ color: styles?.color || 'text.secondary' }}
      />
      <Typography variant='body2'>{description}</Typography>

      {mode === 'edit' && !isAddTileItem && (
        <Stack
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              return onEditTile?.();
            }}
          >
            <ModeIcon sx={{ color: styles?.color || 'text.secondary' }} />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              return onRemoveTile?.();
            }}
          >
            <ClearIcon sx={{ color: styles?.color || 'text.secondary' }} />
          </IconButton>
        </Stack>
      )}
    </Paper>
  );
};

export default Tile;
